import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getWhatsAppTemplates, getGmailTemplates } from '../../api';
import { pushCRMSegment } from '../api';
import { SendMessagePage } from '../../components/SendMessagePage';
import { SendEmailCampaignPage } from '../../components/SendEmailCampaignPage';
import { buildBulkMessagePayload } from '../../utils/buildBulkMessagePayload';
import { TableChip } from '../../components/PremiumDesign';
import { V2Table } from '@/common/components';
import { Button, SearchInput } from '@/components';
import { parseApiError } from '@/common/utils';
import { encodeId } from '@/common/utils/encryption';
import { useDelayedSkeleton } from '@/common/hooks';
import { useMessagingChannels } from '../../hooks/useMessagingChannels';

function BackButton({ onClick, label = 'Back' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-9 h-9 rounded-[8px] bg-white border border-primary text-primary hover:bg-surface-mint flex items-center justify-center transition-colors focus:outline-none shrink-0"
      aria-label={label}
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </button>
  );
}

function categoryLabel(template) {
  const cat = (template?.category || '').toUpperCase();
  if (cat === 'MARKETING') return 'Promotion';
  if (cat === 'AUTHENTICATION') return 'Verification';
  if (cat === 'UTILITY') return 'Notification';
  return 'General';
}

/**
 * Segment Push Flow: Allows selecting channel (WhatsApp or Email),
 * browsing approved templates, and dispatching bulk campaign to group.
 */
export function SegmentPushFlow({ companyId, segment, onClose }) {
  const navigate = useNavigate();
  const integration = useMessagingChannels(companyId);
  const isEmailConnected = Boolean(integration.isReady && integration.email);
  const contactCount = Number(segment?.contact_count) || 0;
  const [channel, setChannel] = useState('whatsapp'); // 'whatsapp' | 'email'

  useEffect(() => {
    if (!integration.isReady) return;
    if (!integration.email && channel === 'email') {
      setChannel('whatsapp');
    }
  }, [integration.isReady, integration.email, channel]);

  const channelAllowed =
    channel === 'email'
      ? isEmailConnected
      : !integration.isReady || integration.whatsapp !== false;
  const [step, setStep] = useState('templates'); // 'templates' | 'compose'
  const [searchQuery, setSearchQuery] = useState('');
  const [templatePage, setTemplatePage] = useState(1);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);

  // 1. WhatsApp Templates Query
  const {
    data: whatsappTemplatesRaw,
    isLoading: isWhatsAppLoading,
    isFetching: isWhatsAppFetching,
  } = useQuery({
    queryKey: ['whatsapp-templates', companyId, searchQuery],
    queryFn: () =>
      getWhatsAppTemplates(companyId, {
        search: searchQuery || undefined,
      }),
    enabled: !!companyId && step === 'templates' && channel === 'whatsapp',
  });

  // 2. Email Templates Query
  const {
    data: emailTemplatesRaw,
    isLoading: isEmailLoading,
    isFetching: isEmailFetching,
  } = useQuery({
    queryKey: ['gmail-templates-push', companyId, searchQuery],
    queryFn: () =>
      getGmailTemplates(companyId, {
        search: searchQuery || undefined,
      }),
    enabled: !!companyId && step === 'templates' && channel === 'email',
  });

  const showWhatsAppSkeleton = useDelayedSkeleton(
    isWhatsAppFetching && whatsappTemplatesRaw !== undefined,
  );
  const isLoadingWhatsApp = isWhatsAppLoading || showWhatsAppSkeleton;

  const showEmailSkeleton = useDelayedSkeleton(
    isEmailFetching && emailTemplatesRaw !== undefined,
  );
  const isLoadingEmail = isEmailLoading || showEmailSkeleton;

  const approvedWhatsAppTemplates = useMemo(() => {
    const list =
      whatsappTemplatesRaw?.results ||
      (Array.isArray(whatsappTemplatesRaw) ? whatsappTemplatesRaw : []);
    return list.filter((t) => (t.status || '').toUpperCase() === 'APPROVED');
  }, [whatsappTemplatesRaw]);

  const emailTemplates = useMemo(() => {
    const list = Array.isArray(emailTemplatesRaw)
      ? emailTemplatesRaw
      : emailTemplatesRaw?.results || emailTemplatesRaw?.data || [];
    return list;
  }, [emailTemplatesRaw]);

  useEffect(() => {
    setTemplatePage(1);
  }, [channel, searchQuery]);

  const templatePageSize = 10;
  const activeTemplates =
    channel === 'email' ? emailTemplates : approvedWhatsAppTemplates;
  const templateRows = activeTemplates.slice(
    (templatePage - 1) * templatePageSize,
    templatePage * templatePageSize,
  );
  const reportedCount =
    channel === 'email'
      ? Number(emailTemplatesRaw?.count)
      : Number(whatsappTemplatesRaw?.count);
  const templatesArePartial =
    Number.isFinite(reportedCount) && reportedCount > activeTemplates.length;

  const templateColumns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
        defaultWidth: 240,
        minWidth: 160,
        cell: (template) => (
          <span className="truncate block" title={template.name}>
            {template.name}
          </span>
        ),
      },
      {
        id: 'meta',
        label: channel === 'email' ? 'Subject' : 'Type',
        defaultWidth: 220,
        minWidth: 140,
        cell: (template) => {
          const value =
            channel === 'email'
              ? template.subject || '(No Subject)'
              : categoryLabel(template);
          return (
            <span className="truncate block" title={value}>
              {value}
            </span>
          );
        },
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 120,
        minWidth: 90,
        cell: () => <TableChip tone="success">Active</TableChip>,
      },
      {
        id: 'action',
        label: 'Action',
        defaultWidth: 110,
        minWidth: 90,
        align: 'center',
        stopPropagation: true,
        cell: (template) => (
          <div className="flex justify-center">
            <Button
              variant="primary"
              size="sm"
              className="!h-8 !px-3 !text-[13px] font-medium"
              onClick={() => {
                if (!channelAllowed) return;
                if (channel === 'email') {
                  setSelectedEmailTemplate(template);
                } else {
                  setSelectedTemplateId(template.id);
                }
                setStep('compose');
              }}
            >
              Use
            </Button>
          </div>
        ),
      },
    ],
    [channel, channelAllowed],
  );

  const selectedWhatsAppTemplate = approvedWhatsAppTemplates.find(
    (t) => t.id === selectedTemplateId,
  );

  // WhatsApp Push Mutation
  const whatsappPushMutation = useMutation({
    mutationFn: (payload) =>
      pushCRMSegment({
        companyId,
        segmentId: segment.id,
        payload,
      }),
    onSuccess: (data) => {
      const campaignId = data?.campaign_id;
      const mapped = data?.mapped_clients;
      const eligible = data?.eligible_contacts;
      const summaryBits = [];
      if (eligible != null) summaryBits.push(`${eligible} eligible`);
      if (mapped != null) summaryBits.push(`${mapped} mapped`);
      toast.success(
        summaryBits.length
          ? `Campaign queued (${summaryBits.join(', ')}).`
          : 'Campaign queued',
        { duration: 4000 },
      );
      if (campaignId) {
        navigate(`/campaigns/${encodeId(campaignId)}`);
      } else {
        onClose();
      }
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail || err?.response?.data?.message || message;
      if (
        status === 400 &&
        typeof detail === 'string' &&
        /opt-?in|phone|recipient|empty/i.test(detail)
      ) {
        toast.error(
          'No WhatsApp recipients. Contacts need opt-in consent and a phone number.',
        );
        return;
      }
      toast.error(message || 'Failed to queue campaign.');
    },
  });

  const handleComposeSend = (_customerIds, messagePayload) => {
    if (!channelAllowed) return;
    const bulkFields = buildBulkMessagePayload({
      templateId: selectedTemplateId,
      messagePayload,
    });
    whatsappPushMutation.mutate({
      adapter_name: 'whatsapp',
      ...bulkFields,
    });
  };

  // Step 2 Compose: WhatsApp
  if (
    step === 'compose' &&
    channelAllowed &&
    channel === 'whatsapp' &&
    selectedWhatsAppTemplate
  ) {
    return (
      <SendMessagePage
        className="groups-send-wa"
        companyId={companyId}
        template={selectedWhatsAppTemplate}
        templateName={selectedWhatsAppTemplate.name}
        audienceMode="segment"
        segmentAudience={{
          name: segment?.name,
          contactCount,
        }}
        onCancel={() => setStep('templates')}
        onSend={handleComposeSend}
        isSending={whatsappPushMutation.isPending}
      />
    );
  }

  // Step 2 Compose: Email
  if (
    step === 'compose' &&
    channelAllowed &&
    channel === 'email' &&
    selectedEmailTemplate
  ) {
    return (
      <SendEmailCampaignPage
        className="groups-send-email"
        companyId={companyId}
        template={selectedEmailTemplate}
        segment={segment}
        onCancel={() => setStep('templates')}
        onSend={(data) => {
          onClose?.();
          const campaignId = data?.id || data?.campaign_id;
          if (campaignId) {
            navigate(`/email-campaigns/${encodeId(campaignId)}`);
          } else {
            navigate('/email-campaigns');
          }
        }}
      />
    );
  }

  // Step 1: Browse Templates (with Channel tabs)
  return (
    <div className="groups-push w-full h-full flex-1 min-h-0 flex flex-col gap-4 animate-fadeIn p-4 sm:p-6 pb-4 bg-layer-5-card rounded-2xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3">
          <BackButton onClick={onClose} />
          <div className="flex flex-col min-w-0">
            <h1 className="text-xl font-bold text-main-black truncate">
              Push group
            </h1>
            <p className="text-sm text-text-secondary truncate">
              {segment?.name || 'Group'} · {contactCount} contacts
            </p>
          </div>
        </div>

        {isEmailConnected ? (
          <div
            className="flex items-center p-1 bg-white border border-border-default rounded-xl w-fit shrink-0"
            role="tablist"
            aria-label="Channel selection"
          >
            <button
              type="button"
              role="tab"
              aria-selected={channel === 'whatsapp'}
              onClick={() => {
                setChannel('whatsapp');
                setSelectedTemplateId(null);
                setSelectedEmailTemplate(null);
              }}
              className={`h-8 px-3.5 sm:px-4 text-[13px] font-semibold rounded-lg transition-all ${
                channel === 'whatsapp'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              WhatsApp
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={channel === 'email'}
              onClick={() => {
                setChannel('email');
                setSelectedTemplateId(null);
                setSelectedEmailTemplate(null);
              }}
              className={`h-8 px-3.5 sm:px-4 text-[13px] font-semibold rounded-lg transition-all ${
                channel === 'email'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Email
            </button>
          </div>
        ) : null}
      </div>

      {contactCount <= 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shrink-0">
          No recipients found in this group. Make sure contacts exist in this
          group before sending campaigns.
        </div>
      )}

      <div className="flex flex-col gap-4 flex-1 min-h-0">
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${channel === 'email' ? 'email' : 'WhatsApp'} templates...`}
          className="w-full sm:max-w-md shrink-0"
        />

        <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col">
          {templatesArePartial ? (
            <p className="text-xs text-text-muted px-1 pb-2 shrink-0">
              Showing {activeTemplates.length} loaded of {reportedCount}. This
              response did not include later pages.
            </p>
          ) : null}
          <V2Table
            tight
            className="groups-table"
            storageKey={
              channel === 'email'
                ? 'segment_push_email_templates_column_widths'
                : 'segment_push_whatsapp_templates_column_widths'
            }
            columns={templateColumns}
            data={templateRows}
            rowKey="id"
            loading={channel === 'email' ? isLoadingEmail : isLoadingWhatsApp}
            empty={
              channel === 'email'
                ? 'No email templates found.'
                : 'No approved WhatsApp templates found.'
            }
            pagination={{
              currentPage: templatePage,
              totalCount: activeTemplates.length,
              itemsPerPage: templatePageSize,
              onChange: setTemplatePage,
            }}
          />
        </div>
      </div>
    </div>
  );
}
